import React, { useState, useEffect } from 'react';
import axiosInstance from '../store/axiosInterceptor';
import { useToast } from '../components/Toast/ToastContext';
import { CardSkeleton } from '../components/Skeleton';

interface Report {
  id: string;
  reporter_id: string;
  reporter_name: string;
  reporter_email?: string;
  reporter_phone?: string;
  reporter_avatar?: string;
  user_id: string;
  user_name: string;
  user_email?: string;
  user_phone?: string;
  user_avatar?: string;
  text: string;
  status: string;
  created_at: string;
}

const ReportsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        '/api/reports/search',
        { query: '' }
      );
      setReports(response.data || []);
    } catch (error: any) {
      showToast('Failed to load reports', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredReports = reports.filter((report) => {
    if (!searchQuery) return true;
    const reporterName = report.reporter_name || '';
    const userName = report.user_name || '';
    return (
      reporterName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleResolveReport = async (id: string) => {
    try {
      await axiosInstance.put(
        `/api/reports/${id}`,
        { status: 'resolved' }
      );
      
      // Update report status in local state
      setReports(prevReports => 
        prevReports.map(report => 
          report.id === id ? { ...report, status: 'resolved' } : report
        )
      );
      
      showToast('Report marked as resolved', 'success');
    } catch (error: any) {
      showToast('Failed to resolve report', 'error');
    }
  };

  const handleReplyStudent = (id: string) => {
    showToast('Reply feature coming soon', 'info');
    // TODO: Implement reply functionality
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Search Skeleton */}
        <div className="h-10 w-80 bg-gray-200 rounded-lg animate-pulse"></div>
        {/* Report Cards Skeleton */}
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="relative w-80">
        <input
          type="text"
          placeholder="Search Coach"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-300 focus:outline-none focus:ring-0"
        />
        <svg
          className="absolute left-3 top-2.5 h-4 w-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.length > 0 ? (
          filteredReports.map((report) => (
            <div
              key={report.id}
              className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]"
            >
              {/* Report Header */}
              <div className="mb-4 flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={report.reporter_avatar || 'https://via.placeholder.com/48'}
                    alt={report.reporter_name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      {report.reporter_name || 'Unknown Reporter'}
                    </h3>
                    <p className="text-sm text-gray-400">
                      reviewed {report.user_name || 'Unknown User'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {/* Status Badge */}
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                    (report.status || 'open') === 'resolved' 
                      ? 'bg-green-100 text-green-700'
                      : (report.status || 'open') === 'closed'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {(report.status || 'open').charAt(0).toUpperCase() + (report.status || 'open').slice(1)}
                  </span>
                  <p className="text-sm text-gray-400 whitespace-nowrap">
                    {new Date(report.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>

              {/* Concern */}
              <p className="mb-4 text-sm leading-relaxed text-gray-900">
                {report.text || 'No details provided'}
              </p>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {(report.status || 'open') !== 'resolved' ? (
                  <button
                    onClick={() => handleResolveReport(report.id)}
                    className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
                  >
                    Resolve
                  </button>
                ) : (
                  <button
                    disabled
                    className="rounded-lg bg-gray-300 px-4 py-2 text-sm font-medium text-gray-500 cursor-not-allowed"
                  >
                    Resolved
                  </button>
                )}
                <button
                  onClick={() => handleReplyStudent(report.id)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Reply Student
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-[#F2F4F7] bg-white py-12 text-center shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
            <p className="text-sm text-gray-400">No reports found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;
